import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Clock, MoonStar, ShieldCheck } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import api from "../lib/api";

const REFRESH_MS = 60_000;
const BADGE_MAX = 99;

function iconFor(kind) {
  if (kind === "task_overdue") return <AlertTriangle className="w-4 h-4 text-rose-600" />;
  if (kind === "task_due_today") return <Clock className="w-4 h-4 text-amber-600" />;
  if (kind === "auditor_hit") return <ShieldCheck className="w-4 h-4 text-[#1A7F6E]" />;
  return <MoonStar className="w-4 h-4 text-slate-500" />;
}

function labelFor(kind) {
  if (kind === "task_overdue") return "Overdue";
  if (kind === "task_due_today") return "Due today";
  if (kind === "auditor_hit") return "Auditor viewed";
  return "Idle";
}

export default function NotificationBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ counts: { total: 0 }, items: [] });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/crm/notifications")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const total = data.counts?.total || 0;
  const overdue = data.counts?.overdue_tasks || 0;
  const dueToday = data.counts?.due_today_tasks || 0;
  const idle = data.counts?.idle_leads || 0;

  function goTo(item) {
    setOpen(false);
    if (item.kind === "idle_lead" || item.kind === "auditor_hit") {
      nav(`/crm?lead=${item.lead_id}`);
    } else {
      nav("/follow-ups");
    }
  }

  const grouped = {
    task_overdue: data.items?.filter((i) => i.kind === "task_overdue") || [],
    task_due_today: data.items?.filter((i) => i.kind === "task_due_today") || [],
    auditor_hit: data.items?.filter((i) => i.kind === "auditor_hit") || [],
    idle_lead: data.items?.filter((i) => i.kind === "idle_lead") || [],
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex items-center justify-center w-9 h-9 rounded-md text-slate-600 hover:text-[#0D1B2A] hover:bg-slate-100"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {total > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notification-badge"
            >
              {total > BADGE_MAX ? `${BADGE_MAX}+` : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Alerts</p>
            <h3 className="text-sm font-semibold text-[#0D1B2A]">Your action list</h3>
          </div>
          <div className="text-xs text-slate-500 space-x-2">
            <span className="text-rose-600 font-medium">{overdue}</span>
            <span>·</span>
            <span className="text-amber-600 font-medium">{dueToday}</span>
            <span>·</span>
            <span className="text-slate-600 font-medium">{idle}</span>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {total === 0 && (
            <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notification-empty">
              You&apos;re all caught up. Nothing overdue, due today, or idle.
            </div>
          )}
          {[
            ["task_overdue", "Overdue tasks"],
            ["task_due_today", "Due today"],
            ["auditor_hit", "Auditor accessed suite"],
            ["idle_lead", `Idle leads (14+ days)`],
          ].map(([groupKey, groupLabel]) => (
            grouped[groupKey].length > 0 && (
              <div key={groupKey}>
                <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-slate-500">
                  {groupLabel}
                </div>
                {grouped[groupKey].map((item) => (
                  <button
                    type="button"
                    key={`${item.kind}-${item.id}`}
                    onClick={() => goTo(item)}
                    className="w-full text-left px-4 py-3 border-t border-slate-100 hover:bg-[#E6F4F1]/40 flex items-start gap-2"
                    data-testid={`notification-item-${item.id}`}
                  >
                    <span className="mt-0.5">{iconFor(item.kind)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-[#0D1B2A] truncate">
                        {item.kind === "idle_lead" || item.kind === "auditor_hit" ? item.org_name : item.description}
                      </span>
                      <span className="block text-xs text-slate-500 truncate">
                        {item.kind === "idle_lead"
                          ? `No activity for ${item.days_idle} days · stage: ${item.stage || "—"}`
                          : item.kind === "auditor_hit"
                            ? `Auditor viewed the document suite`
                            : `${item.org_name} · ${labelFor(item.kind)}${item.due_date ? ` (${item.due_date})` : ""}`}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )
          ))}
        </div>
        <div className="border-t border-slate-200 px-4 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => { setOpen(false); nav("/follow-ups"); }}
            className="text-xs text-[#1A7F6E] hover:underline"
            data-testid="notification-open-followups"
          >
            Open Follow-ups →
          </button>
          <button
            type="button"
            onClick={load}
            className="text-xs text-slate-500 hover:text-[#0D1B2A]"
            data-testid="notification-refresh"
          >
            Refresh
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
